import React, { useState, useEffect } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import {
    fetchAssignees,
    postMessage,
    createCampaignRequestNote,
} from '~/services/campaign';
import Select from 'react-select';
import { DropdownIndicatorProps, components } from 'react-select';
import TipTapEditor from '~/components/tiptap/TipTapEditor';

enum ADD_MSG_TYPE {
    ONLY_TODO = 'ONLY_TODO',
    ONLY_NOTE = 'ONLY_NOTE',
}

interface Props {
    type: ADD_MSG_TYPE;
    campaign?: any;
    campaignRequest?: any;
    setNotes: any;
    onClose: () => void;
}

interface FormValues {
    todo_flag: boolean;
    assignTo: { label: string; value: string };
    note: string;
}

const AddNoteInlineForm: React.FC<Props> = ({
    type,
    campaign = null,
    campaignRequest = null,
    setNotes,
    onClose,
}) => {
    const [assigneeOptions, setAssigneeOptions] = useState([]);
    const [saving, setSaving] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
    } = useForm<FormValues>({
        defaultValues: { todo_flag: type === ADD_MSG_TYPE.ONLY_TODO },
        mode: 'onBlur',
    });
    const todo_flag = watch('todo_flag');

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        if (!data?.note) return;

        try {
            setSaving(true);

            // If we have a campaign request (and no campaign), use the campaign request notes endpoint
            if (campaignRequest && !campaign?.campaign_id && !campaign?.id) {
                const notePayload = {
                    note: data.note,
                    assignment: data?.assignTo?.value
                        ? parseInt(data.assignTo.value)
                        : null,
                    todo_flag: data?.todo_flag || false,
                };

                const res = await createCampaignRequestNote(
                    campaignRequest.id,
                    notePayload,
                );

                // Keep the whole serialized note: campaign_request is what routes
                // later edits/pins/deletes to the CR note endpoints instead of
                // /messages (whose ids collide), and assignment fills the edit form.
                setNotes((prev: any) => [{ ...res.note, tags: '' }, ...prev]);
            } else {
                // Original message creation logic for campaigns
                const campaignId = campaign?.campaign_id || campaign?.id;
                const campaignName = campaign?.campaign_name || campaign?.name;

                const payload = {
                    campaign_name: campaignName || '',
                    note: data?.note,
                    todo_flag: data?.todo_flag,
                    pinned_flag: false,
                    fc_acknowledged_flag: false,
                    created_date: new Date(),
                    campaign: campaignId || null,
                    assignee: data?.assignTo?.value
                        ? parseInt(data.assignTo.value)
                        : null,
                };

                const res = await postMessage(payload);
                setNotes((prev: any) => [res, ...prev]);
            }

            reset({ todo_flag: type === ADD_MSG_TYPE.ONLY_TODO });
            onClose();
        } catch (error) {
            console.log(error);
        } finally {
            setSaving(false);
        }
    };

    const loadAssigneeOptions = async () => {
        const assignees = await fetchAssignees();

        const mappedAssignees = assignees?.flatMap((group: any[]) => {
            const groupMembers = group[1];

            return groupMembers.map((member: any) => ({
                value: member[0],
                label: member[1],
            }));
        });

        setAssigneeOptions(mappedAssignees);
        return mappedAssignees;
    };

    useEffect(() => {
        loadAssigneeOptions();
    }, []);

    const DropdownIndicator: React.FC<DropdownIndicatorProps> = (props) => {
        return (
            <components.DropdownIndicator {...props}>
                <svg
                    className='h-4 w-4 text-gray-500 dark:text-gray-400'
                    aria-hidden='true'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 20 20'
                >
                    <path
                        stroke='currentColor'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z'
                    />
                </svg>
            </components.DropdownIndicator>
        );
    };

    return (
        <div className='rounded-lg border-2 border-gray-200 bg-gray-50 p-3'>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className='mb-2 flex items-start'>
                    <Controller
                        name='todo_flag'
                        control={control}
                        render={({ field }) => (
                            <input
                                disabled={type === ADD_MSG_TYPE.ONLY_TODO}
                                checked={field.value}
                                onChange={(e) =>
                                    field.onChange(e.target.checked)
                                }
                                type='checkbox'
                                className='mr-2 mt-1.5 text-sm'
                                id='completed'
                            />
                        )}
                    />
                    <label className='text-xs' htmlFor='completed'>
                        <span className='block text-base'>Mark as To Do</span>
                        <span className='block'>
                            If selected the message must be assigned to someone
                        </span>
                    </label>
                </div>

                <div className='mb-2'>
                    <label className='block text-xs font-semibold'>
                        Assign To
                        {todo_flag && <span className='text-red'> *</span>}
                    </label>
                    <div className='relative'>
                        <Controller
                            name='assignTo'
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (watch('todo_flag') && !value) {
                                        return 'Assignee is required when marked as To Do';
                                    }
                                    return true;
                                },
                            }}
                            render={({ field }) => (
                                <Select
                                    menuPlacement='auto'
                                    {...field}
                                    placeholder='Select Assignee (Optional)'
                                    isClearable
                                    options={assigneeOptions}
                                    styles={{
                                        control: (baseStyles, state) => ({
                                            ...baseStyles,
                                            borderColor: errors.assignTo
                                                ? 'red'
                                                : 'black',
                                            borderRadius: '0.375rem',
                                            backgroundColor: 'white',
                                        }),
                                        menu: (baseStyles) => ({
                                            ...baseStyles,
                                            zIndex: 9999,
                                        }),
                                    }}
                                    components={{
                                        IndicatorSeparator: null,
                                        DropdownIndicator,
                                    }}
                                />
                            )}
                        />
                    </div>
                    {errors.assignTo && (
                        <p className='mt-1 text-xs text-red'>
                            {errors.assignTo.message}
                        </p>
                    )}
                </div>

                <div className='mb-2'>
                    <label className='block text-xs font-semibold'>
                        Message
                    </label>
                    <Controller
                        control={control}
                        name='note'
                        rules={{
                            required: 'Message is required',
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <TipTapEditor
                                    initialValue={value}
                                    onChange={onChange}
                                />
                            );
                        }}
                    />
                    {errors.note && (
                        <p className='mt-1 text-xs text-red'>
                            {errors.note.message}
                        </p>
                    )}
                </div>

                <div className='flex justify-start space-x-2'>
                    <button
                        type='button'
                        onClick={onClose}
                        className='rounded-full border-[2px] px-[20px] py-[6px] text-[13px]/[16px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                    >
                        Cancel
                    </button>
                    <button
                        type='submit'
                        disabled={saving}
                        className='rounded-full bg-warning px-[20px] py-[6px] text-[13px]/[16px] font-bold uppercase text-black transition-colors duration-500 hover:bg-yellow disabled:opacity-50'
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddNoteInlineForm;
export { ADD_MSG_TYPE };
